/**
 * useVendorNotificationToasts
 *
 * Polls for new vendor notifications and surfaces them as react-hot-toast
 * custom toasts with deep-link navigation on click.
 *
 * Only active for VENDOR and VENDOR_STAFF roles.
 * Shares the same React Query cache key as useNotifications so there is no
 * duplicate network traffic when the notification bell is also mounted.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  type Notification as AppNotification,
  type NotificationListResponse,
  NotificationType,
  Role,
} from '@lov/shared';
import { useAuth } from '../../../contexts/AuthContext';
import { validatedApi } from '../../../lib/api/validated-client';
import { notificationKeys } from './useNotifications';
import { getNotificationRoute } from '../utils/getNotificationRoute';
import { NotificationToast } from '../components/NotificationToast';
import { playNotificationSound } from '../utils/notificationSound';

// Notification types that warrant an immediate toast alert for vendor owners
const VENDOR_TOAST_TYPES = new Set<NotificationType>([
  NotificationType.VENDOR_NEW_BOOKING,
  NotificationType.BOOKING_CANCELLED,
  NotificationType.PAYMENT_SUCCESS,
  NotificationType.PAYMENT_FAILED,
  NotificationType.PAYOUT_SUCCESS,
  NotificationType.PAYOUT_FAILURE,
]);

// Staff only see a subset — no payout or new-booking owner-level notifications
const STAFF_TOAST_TYPES = new Set<NotificationType>([
  NotificationType.BOOKING_CANCELLED,
  NotificationType.PAYMENT_SUCCESS,
  NotificationType.PAYMENT_FAILED,
]);

const MAX_TOASTS = 3;
const TOAST_DURATION_MS = 8000;

export function useVendorNotificationToasts(): void {
  const { user } = useAuth();
  const navigate = useNavigate();
  const lastSeenIdRef = useRef<string | null>(null);
  const initializedRef = useRef(false);
  const permissionToastShownRef = useRef(false);

  const isVendor = user?.role === Role.VENDOR || user?.role === Role.VENDOR_STAFF;

  // Use the same query key shape as useNotifications({ }) so this hook shares
  // the in-flight request / cache entry with any mounted notification bell.
  const { data: notificationsData } = useQuery({
    queryKey: notificationKeys.list({}),
    queryFn: async () => validatedApi.get<NotificationListResponse>('/notifications'),
    enabled: isVendor,
    refetchInterval: 60 * 1000,
  });

  // notificationsData is NotificationListResponse (axios interceptor strips the
  // outer { success, data, meta } envelope, but NotificationListResponse itself
  // has a .data array for the paginated notification items).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const notifications: AppNotification[] = notificationsData?.data ?? [];

  // On first successful load, record the newest id so we do NOT toast
  // notifications that were already present before this session started.
  useEffect(() => {
    if (!isVendor || initializedRef.current || notifications.length === 0) return;
    lastSeenIdRef.current = notifications[0].id;
    initializedRef.current = true;
  }, [isVendor, notifications]);

  const showNotificationToast = useCallback(
    (notification: AppNotification) => {
      const route = getNotificationRoute(notification, user?.role);
      toast.custom(
        (t) => (
          <NotificationToast
            notification={notification}
            onView={() => {
              toast.dismiss(t.id);
              if (route) navigate(route);
            }}
            onDismiss={() => toast.dismiss(t.id)}
          />
        ),
        {
          duration: TOAST_DURATION_MS,
          position: 'top-right',
          id: `notif-${notification.id}`,
        }
      );

      // Fire a native browser notification when the user has granted permission.
      // This surfaces the alert even when the tab is minimised (but still open).
      // window.Notification is the browser global — distinct from the AppNotification
      // domain type imported above.
      if (
        typeof window !== 'undefined' &&
        'Notification' in window &&
        window.Notification.permission === 'granted'
      ) {
        try {
          new window.Notification(notification.title, {
            body: notification.body,
            icon: '/favicon.ico',
          });
        } catch {
          // Silently fail — some browser contexts (iframes, webviews) block the
          // Notification constructor even when permission is 'granted'.
        }
      }
    },
    [navigate, user]
  );

  // After initialization, diff the notification list against the last-seen id.
  // Any notifications that appear before the last-seen entry are new.
  useEffect(() => {
    if (!isVendor || !initializedRef.current || notifications.length === 0) return;

    const lastSeenId = lastSeenIdRef.current;
    if (!lastSeenId) return;

    const lastSeenIndex = notifications.findIndex((n) => n.id === lastSeenId);

    // If the last-seen id has scrolled off the current page the list has shifted
    // entirely — skip to avoid a flood of false-positive toasts.
    if (lastSeenIndex === -1) return;

    const newNotifications = notifications.slice(0, lastSeenIndex);

    if (newNotifications.length === 0) return;

    // Advance the cursor so we only toast each notification once.
    lastSeenIdRef.current = newNotifications[0].id;

    const toastTypes = user?.role === Role.VENDOR_STAFF ? STAFF_TOAST_TYPES : VENDOR_TOAST_TYPES;
    const toastable = newNotifications.filter((n) => toastTypes.has(n.type)).slice(0, MAX_TOASTS);

    if (toastable.length > 0) {
      playNotificationSound();
    }
    toastable.forEach((n) => showNotificationToast(n));

    if (
      toastable.length > 0 &&
      typeof window !== 'undefined' &&
      typeof window.Notification !== 'undefined' &&
      window.Notification.permission === 'default' &&
      !permissionToastShownRef.current
    ) {
      permissionToastShownRef.current = true;
      toast.custom(
        () => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>🔔</span>
            <span>
              Enable browser notifications to see alerts even when this tab is in background
            </span>
            <button onClick={() => window.Notification.requestPermission()}>Enable</button>
          </div>
        ),
        {
          id: 'notif-permission-prompt',
        }
      );
    }
  }, [isVendor, notifications, showNotificationToast, user]);
}
